import {
  ApplicationRef,
  ComponentFactoryResolver,
  EmbeddedViewRef,
  Injectable,
  Injector,
  OnDestroy,
} from '@angular/core';
import {
  CollectionHelper,
  CompareHelper,
  ConvertHelper,
  DotNetBridge,
  ProfileInfoViewModel,
  StringHelper,
} from '@barthauer/abc/core';
import { LoginInfo, ProfileInfo, ProfileService, UserService } from '@barthauer/abc/core/authentication';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import {
  CoreDataEntity,
  DataService as CoreDataService,
  DeleteObjectsModel,
  ICoreDataEntity,
  IPersistable,
  ObjectDeleteBehaviour,
  ObjectKey,
  ObjectTypeInfoViewModel,
  PersistenceService,
  PersistMode,
  QueryHelper,
} from '@barthauer/abc/core/data';
import { ErrorInfo } from '@barthauer/abc/core/error-handling';
import { ResourceManager } from '@barthauer/abc/core/resources';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';
import { ParameterDefinition, ParameterValue } from '@barthauer/abc/queries/parameters/core';

import { ToastType } from '@barthauer/abc/ui-components';
import { ParameterService } from 'libs/abc/queries/parameters/values-editor/src/lib/services/parameter.service';
import { AbcGridContextMenuManager } from 'libs/abc/ui-components/src/lib/core/src/abc-grid/abc-grid-context-menu-manager';
import { Observable, Subject } from 'rxjs';
import {
  AbcArenaService,
  ArenaComponentActions,
  ArenaUIConfiguration,
  LayoutUsage,
  NavStateItem,
  Status,
} from '../abc-arena.service';
import { ArenaSetting } from '../models/arena-settings.model';
import { ArenaSettingsService } from '../services/arena-settings.service';
import { DataService } from '../services/data.service';
import { ArenaCacheSettings } from './models/setting.model';
import { NavigationDocument, NavigationGroup } from './navigation/navigation.model';
import { NavigationLayoutViewModel, NavigationViewModel } from './navigation/navigation.viewModel';
import { LayoutViewModel } from './viewModels/layout.viewModel';

@Injectable({
  providedIn: 'root',
})
export class ArenaService implements OnDestroy {
  public get actions(): ArenaComponentActions {
    return this.abcArenaService.actions;
  }
  public get arenaName(): string {
    return this.uiconfig?.arenaName ?? this.configService.configuration.DefaultRoute;
  }
  public get uiconfig(): ArenaUIConfiguration {
    return this.actions.getUIConfiguration();
  }
  public parametersWindowSubject = new Subject<{
    layoutId: string;
    displayParametersWindow: boolean;
  }>();
  public parametersWindow$ = this.parametersWindowSubject.asObservable();
  public rtlEnabled: boolean;
  private hignaScrollToTopSubject = new Subject<any>();
  private addPanelSubject = new Subject<any>();
  private dataFormAddObjectForDeleteSubject = new Subject<any>();
  private dataFormLayoutsSubject = new Subject<any>();
  private navigationNodeClickSubject = new Subject();
  public persistMode: PersistMode;
  public notificationSubject: Subject<Notification> = new Subject();
  private destroyPDCSubject = new Subject();
  private updateActionsAndInfoDetailsSubject = new Subject();
  private destroyTileNavSubject = new Subject();
  private onSettingsChangedSubject = new Subject();
  public expandNode = new Subject();
  public navigationViewModelData: NavigationViewModel;
  public loadDataForm: boolean;
  public loadHigna: boolean;
  public loadContextMenu: boolean;
  public destroyObservables = new Subject<void>();
  public persistDocumentImagesSubject = new Subject<any>();
  public selectedNavMenuIndex = -1;
  public componentRef: any;
  public hignaScrollToTop$ = this.hignaScrollToTopSubject.asObservable();
  public addPanel$ = this.addPanelSubject.asObservable();
  public addObjectForDelete$ = this.dataFormAddObjectForDeleteSubject.asObservable();
  public dataFormLayouts$ = this.dataFormLayoutsSubject.asObservable();
  public navigationNodeClick$ = this.navigationNodeClickSubject.asObservable();
  public ensureInitiateDataFormSubject = new Subject();
  public ensureInitiateDataform$ = this.ensureInitiateDataFormSubject.asObservable();
  public notification$ = this.notificationSubject.asObservable();
  public destroyPDC$ = this.destroyPDCSubject.asObservable();
  public updateActionAndInfoDetails$ = this.updateActionsAndInfoDetailsSubject.asObservable();
  public destroyTileNav$ = this.destroyTileNavSubject.asObservable();
  public expandNode$ = this.expandNode.asObservable();
  public onSettingsChanged$ = this.onSettingsChangedSubject.asObservable();
  public res = ResourceManager.getResources('abc-arena');
  /**
   * @deprecated Use AbcArenaService instead!
   */
  public additionalMenuActions: any;
  public externalComponent: boolean = false;
  public readonly PERSISTENCE_CATEGORY = 'ArenaDataForm';
  public readonly DOCUMENTS_PERSISTENCE_CATEGORY = 'DocumentsEditorCardComponent';
  public readonly COMMENTS_PERSISTENCE_CATEGORY = 'CommentsService';
  public readonly PROPERTIES_PERSISTENCE_CATEGORY = 'PropertiesService';

  public insertedDataEntity: (entity: ICoreDataEntity) => Promise<void>;
  public updatedDataEntity: (entity: ICoreDataEntity) => Promise<void>;
  public deletedDataEntity: (entity: ICoreDataEntity) => Promise<void>;
  private deletingDataEntity: ICoreDataEntity;

  public updateHigonOnSave: () => Promise<void>;

  public static readonly InvisibleMainGroupPrefix = '#';
  public static readonly LayoutPathByLayoutIdPrefix = '!';

  constructor(
    private dataService: DataService,
    private coreDataService: CoreDataService,
    private componentFactoryResolver: ComponentFactoryResolver,
    private appRef: ApplicationRef,
    private injector: Injector,
    private configService: AbcConfigService,
    private abcArenaService: AbcArenaService,
    private userService: UserService,
    private persistenceService: PersistenceService,
    private profileService: ProfileService,
    private parameterService: ParameterService,
    private messageBox: MessageBoxService,
    private arenaSettingsService: ArenaSettingsService,
  ) {
    this.rtlEnabled = ResourceManager.getLanguage() === 'ar' ? true : false;

    this.parameterService.loadParameterDefinitions = async () => {
      return await this.loadParameterDefinitions();
    };
    this.parameterService.saveParameterDefinitions = async (parameterDefinitions) => {
      await this.saveParameterDefinitions(parameterDefinitions);
    };

    this.persistenceService.afterPersistPendingChanges$.subscribe({
      next: (object) => {
        if (object instanceof ErrorInfo) {
          if (this.persistMode === PersistMode.Delete) {
            this.handleDeepDeletion(this.deletingDataEntity);
          } else {
            this.notificationSubject.next({
              type: object.isWarning ? ToastType.Warning : ToastType.Error,
              message: object.message,
            });
          }
        } else if (object?.find((_) => _.persistenceCategory === this.PERSISTENCE_CATEGORY)) {
          for (const e of object) {
            if (this.persistMode === PersistMode.Insert && this.insertedDataEntity) {
              this.insertedDataEntity({
                Id: e.id,
                ObjectType: e.objectType,
              });
            } else if (this.persistMode === PersistMode.Update && this.updatedDataEntity) {
              this.updatedDataEntity({
                Id: e.id,
                ObjectType: e.objectType,
              });
            } else if (this.persistMode === PersistMode.Delete && this.deletedDataEntity) {
              this.deletedDataEntity({
                Id: e.id,
                ObjectType: e.objectType,
              });
            }
          }
          if (this.persistMode === PersistMode.Insert || this.persistMode === PersistMode.Update) {
            this.notificationSubject.next({
              type: ToastType.Success,
              message: this.res('Arena-Success'),
            });

            let dataEntities: Array<CoreDataEntity> = [];
            object.forEach((o) => {
              dataEntities.push(new CoreDataEntity(o.objectType, o.id));
            });
            this.persistDocumentImagesSubject.next(dataEntities[0].id);
          } else if (this.persistMode === PersistMode.Delete) {
            this.notificationSubject.next({
              type: ToastType.Info,
              message: this.res('Arena-ObjectDeleted'),
            });
          }
        } else if (
          object?.find(
            (_) =>
              _.persistenceCategory === this.DOCUMENTS_PERSISTENCE_CATEGORY ||
              _.persistenceCategory === this.COMMENTS_PERSISTENCE_CATEGORY ||
              _.persistenceCategory === this.PROPERTIES_PERSISTENCE_CATEGORY,
          )
        ) {
          this.updateHigonOnSave();
        }
        this.deletingDataEntity = null;
      },
    });
  }

  public ngOnDestroy(): void {
    console.log('Arena Service destroyed');
  }

  public setCacheSettings(settings: ArenaCacheSettings) {
    this.dataService.cacheSettings = settings;
  }

  public getCacheSettings(): ArenaCacheSettings {
    return this.dataService.cacheSettings;
  }

  public settingsChanged(): void {
    this.onSettingsChangedSubject.next(null);
  }

  public async isDotNetStandalone(): Promise<boolean> {
    return DotNetBridge.hasSettings && (await DotNetBridge.settings.get('IsStandalone'));
  }

  public callHignaScrollToTop() {
    this.hignaScrollToTopSubject.next(null);
  }

  public callNavigationNodeClick() {
    this.navigationNodeClickSubject.next(null);
  }

  public deleteDataEntity(currentDataEntity: ICoreDataEntity) {
    this.persistMode = PersistMode.Delete;
    this.deletingDataEntity = currentDataEntity;
    const objectToDelete: IPersistable = {
      id: currentDataEntity.Id,
      objectType: currentDataEntity.ObjectType,
      persistenceCategory: this.PERSISTENCE_CATEGORY,
    };
    this.persistenceService.addObjectForDelete(objectToDelete);
    this.persistenceService.persistPendingChanges(this.PERSISTENCE_CATEGORY);
  }

  public async handleDeepDeletion(dataEntity: ICoreDataEntity) {
    const settings = new MessageSettings();
    settings.mode = MessageMode.Warning;
    settings.button = MessageBoxButtons.YesNo;
    settings.message = this.res('Arena-DeleteObjectConfirmation');
    settings.caption = 'Löschen erzwingen?';
    const dialogResult: DialogResult = await this.messageBox.showMessageBox(settings).toPromise();

    if (dialogResult['result'] === DialogResult.Yes) {
      const deleteObjectsQuery = this.createDeleteObjectsQuery(dataEntity);
      try {
        await this.coreDataService.executeDeleteObjectQuery(deleteObjectsQuery).toPromise();
        this.deletedDataEntity(dataEntity);
      } catch (error) {
        this.notificationSubject.next({
          type: ToastType.Error,
          message: error.message,
        });
        await this.actions.refreshArenaObject(dataEntity);
      }
    } else {
      this.messageBox.dialog.closeAll();
      await this.actions.refreshArenaObject(dataEntity);
    }
  }

  private createDeleteObjectsQuery(dataEntity: ICoreDataEntity): DeleteObjectsModel {
    const deleteObjectsModel: DeleteObjectsModel = new DeleteObjectsModel();
    const objectKey: ObjectKey = new ObjectKey();
    objectKey.Id = dataEntity?.Id;
    objectKey.ObjectType = dataEntity?.ObjectType;
    deleteObjectsModel.ObjectKeys = [objectKey];
    deleteObjectsModel.Behaviour = ObjectDeleteBehaviour.Default;
    return deleteObjectsModel;
  }

  public callUpdateActionsAndInfoDetails(data) {
    this.updateActionsAndInfoDetailsSubject.next(data);
  }

  private async loadPredefinedParameterDefinitions(arenaName?: string): Promise<ParameterDefinition[]> {
    const nav = await this.getNavigation(arenaName ?? this.arenaName);
    return nav?.parameterDefinitions ?? [];
  }

  public async loadParameterDefinitions(arenaName?: string): Promise<ParameterDefinition[]> {
    const predefined = await this.loadPredefinedParameterDefinitions(arenaName);
    const result: ParameterDefinition[] = [];
    if (predefined) {
      for (const pd of predefined) {
        result.push(pd);
      }
    }
    const storage = await this.arenaSettingsService.createStorage(ArenaSetting.parameters);
    const custom: ParameterDefinition[] = await storage.getItem(arenaName ?? this.arenaName);
    if (custom) {
      for (const pd of custom) {
        if (!result?.find((_) => _.Name === pd.Name)) {
          pd.IsUserCreated = true;
          result.push(pd);
        }
      }
    }
    return result;
  }

  public async saveParameterDefinitions(parameterDefinitions: ParameterDefinition[]): Promise<void> {
    const custom: ParameterDefinition[] = [];
    const predefined = await this.loadPredefinedParameterDefinitions();
    if (parameterDefinitions) {
      for (const pd of parameterDefinitions) {
        if (!predefined?.find((_) => _.Name === pd.Name) && pd.IsUserCreated) {
          custom.push(pd);
        }
      }
    }
    const storage = await this.arenaSettingsService.createStorage(ArenaSetting.parameters);
    storage.setItem(this.arenaName, custom);
  }

  public async evaluateLayoutParameters(
    layoutViewModel: LayoutViewModel,
    layoutFilter: string,
    parameterDefinitions: ParameterDefinition[],
    parameterValues: ParameterValue[],
  ): Promise<ParameterValue[]> {
    this.parameterService.hideOnOutsideClick = true;

    const usedParameterDefinitions: ParameterDefinition[] = [];
    if (parameterDefinitions) {
      for (const pd of parameterDefinitions) {
        if (
          this.isParameterUsedInExpression(layoutFilter, pd) ||
          (await this.isParameterUsedInLayout(layoutViewModel, pd))
        ) {
          usedParameterDefinitions.push(pd);
        }
      }
    }
    const effectiveParameters = this.parameterService.tryFindChangeableParameters(
      usedParameterDefinitions,
      parameterValues?.map((_) => _.Name),
    );

    return await this.parameterService.fillParametersIfExist(effectiveParameters);
  }

  private async isParameterUsedInLayout(
    layoutViewModel: LayoutViewModel,
    parameterDefinition: ParameterDefinition,
  ): Promise<boolean> {
    if (layoutViewModel) {
      if (this.isParameterUsedInExpression(layoutViewModel.oPath, parameterDefinition)) {
        return true;
      }
      for (const c of layoutViewModel.allColumns) {
        if (this.isParameterUsedInExpression(c.oPath, parameterDefinition)) {
          return true;
        }
      }
      for (const vw of layoutViewModel.subLayoutViews) {
        if (this.isParameterUsedInExpression(vw.opath, parameterDefinition)) {
          return true;
        }
        for (const vwc of vw.columns) {
          if (this.isParameterUsedInExpression(vwc.oPath, parameterDefinition)) {
            return true;
          }
        }
      }
      const gridLayoutStorage = await this.arenaSettingsService.createStorage(ArenaSetting.gridLayouts);
      const key = this.dataService.getStateStorageKey(this.arenaName, layoutViewModel.id);
      const state = await gridLayoutStorage?.getItem(key);
      if (state?.columns) {
        for (const c of state.columns) {
          if (
            this.isParameterUsedInExpression(c[AbcGridContextMenuManager.CustomOPathColumnKey], parameterDefinition)
          ) {
            return true;
          }
        }
      }
    }
    return false;
  }

  private isParameterUsedInExpression(opath: string, parameterDefinition: ParameterDefinition): boolean {
    return QueryHelper.isParameterNameInExpression(opath, parameterDefinition?.Name);
  }

  private async loadLayoutCore(
    arenaName: string,
    getLayout: (navViewModel: NavigationViewModel) => Promise<NavigationLayoutViewModel | string>,
    layoutDisplayText: string,
    additionalOPath: string,
    idList?: string[],
    navstate?: NavStateItem[],
  ): Promise<void> {
    try {
      const navViewModel = await this.loadWebArena(arenaName, true);

      let rootLayout: NavigationLayoutViewModel;
      if (navstate?.length && navstate[0].layoutPath) {
        rootLayout = navViewModel.findLayoutByFullName(navstate[0].layoutPath, false);
      }

      if (navViewModel) {
        this.navigationViewModelData = navViewModel;
        const layout = await getLayout(navViewModel);
        if (layout instanceof NavigationLayoutViewModel) {
          const layoutFilter = this.dataService.linkOPathExpressions(layout.nodeFilter, [additionalOPath], 'AND');
          await this.actions.updateCurrentNavigationNode(rootLayout ?? layout);
          const lvm = await this.loadArenaLayout(
            layout.layoutName,
            layout.fullName,
            layoutFilter,
            idList,
            await this.loadParameterDefinitions(arenaName),
            navstate,
          );
        } else if (typeof layout === 'string') {
          const navLayout = navViewModel.findBestLayoutByLayoutId(layout);
          await this.actions.updateCurrentNavigationNode(rootLayout ?? navLayout);
          const layoutFilter = this.dataService.linkOPathExpressions(null, [additionalOPath], 'AND');
          const lvm = await this.loadArenaLayout(
            layout,
            navLayout?.fullName,
            layoutFilter,
            idList,
            await this.loadParameterDefinitions(arenaName),
            navstate,
          );
        } else if (layoutDisplayText && layoutDisplayText != undefined) {
          const group = navViewModel.findGroupByFullName(layoutDisplayText, true);
          if (group) {
            await this.actions.updateCurrentNavigationNode(group);
          } else {
            this.actions.updateCurrentNavigationNode(undefined);
            console.log(
              this.res('Arena-WrongLayout') + `: ${layout},` + this.res('Arena-LayoutPath') + `: ${layoutDisplayText}`,
            );
            const message =
              this.res('Arena-WrongLayout') + `, ` + this.res('Arena-LayoutPath') + `: ${layoutDisplayText}`;
            this.notificationSubject.next({
              type: ToastType.Error,
              message: message,
            });
            this.abcArenaService.arenaStatus({
              Status: Status.Error,
              Message: message,
            });
          }
          this.abcArenaService.isNavigating = false;
        }
      } else {
        await this.loadLayoutCore(arenaName, getLayout, layoutDisplayText, additionalOPath, idList);
      }
    } catch (error) {
      console.error(error);
      this.abcArenaService.arenaStatus({
        Status: Status.Error,
        Message: this.res('Arena-CouldntLoadLayout') + `: ${layoutDisplayText}. Error: ${error.message}`,
      });
    }
  }

  public async getNavigation(arenaName: string): Promise<NavigationViewModel> {
    const navViewModel: NavigationViewModel = await this.dataService.getNavigation(arenaName, (result) =>
      this.prepareNavigation(result),
    );
    return navViewModel;
  }

  public async loadGroupOrLayout(
    arenaName: string,
    groupOrLayoutPath: string | NavigationLayoutViewModel,
    idList?: string[],
    navstate?: NavStateItem[],
  ): Promise<void> {
    return await this.loadLayoutCore(
      arenaName,
      async (navViewModel) => {
        const result =
          groupOrLayoutPath instanceof NavigationLayoutViewModel
            ? groupOrLayoutPath
            : this.resolveLayoutPath(navViewModel, groupOrLayoutPath);

        const objecttype = this.abcArenaService.context.nextDataContext.objecttype;
        if (objecttype) {
          const ot = await this.dataService.abcCore.objectSpace.getObjectType(objecttype).toPromise();
          const layouts = this.navigationViewModelData?.findLayoutsByObjectType(ot.name);
          if (layouts.length) {
            let path: string;
            if (result instanceof NavigationLayoutViewModel) {
              path = result.fullName;
            } else {
              path = result;
            }
            for (const layout of layouts) {
              if (layout.fullName === path) {
                return layout;
              }
            }
            return layouts[0];
          }
        }

        return result;
      },
      groupOrLayoutPath instanceof NavigationLayoutViewModel ? groupOrLayoutPath.caption : groupOrLayoutPath,
      null,
      idList,
      navstate,
    );
  }

  private resolveLayoutPath(navigation: NavigationViewModel, layoutPath: string): NavigationLayoutViewModel | string {
    if (layoutPath) {
      if (layoutPath.startsWith(ArenaService.LayoutPathByLayoutIdPrefix)) {
        return layoutPath.substring(ArenaService.LayoutPathByLayoutIdPrefix.length);
      } else {
        return navigation?.findLayoutByFullName(layoutPath, false);
      }
    }
  }

  private async loadArenaLayout(
    layoutName: string,
    layoutPath: string,
    filter: string,
    objectIds: string[],
    parameterDefinitions: ParameterDefinition[],
    navstate: NavStateItem[],
  ): Promise<LayoutViewModel> {
    const lvm = await this.dataService.getLayout(this.arenaName, layoutName);
    const crud = this.abcArenaService.crudPermissions;

    if (crud) {
      lvm.denyInsert = crud.DenyInsert ?? lvm.denyInsert;
      lvm.denyUpdate = crud.DenyUpdate ?? lvm.denyUpdate;
      lvm.denyDelete = crud.DenyDelete ?? lvm.denyDelete;
      lvm.isReadOnly = crud.IsReadOnly ?? lvm.isReadOnly;
    }

    const parameterValues = await this.evaluateLayoutParameters(
      lvm,
      filter,
      parameterDefinitions,
      navstate?.length > 0 ? navstate[navstate.length - 1].parameterValues : undefined,
    );
    await this.actions.loadArenaLayout(lvm, layoutPath, filter, objectIds, parameterValues, navstate);

    return lvm;
  }

  public async loadWebArena(arenaName: string, loadOnly?: boolean): Promise<NavigationViewModel> {
    const navDoc = await this.getNavigation(arenaName ?? this.arenaName);
    this.navigationViewModelData = navDoc;
    this.actions.loadArenaNavigation(navDoc, loadOnly);
    return navDoc;
  }

  private async prepareNavigation(result: NavigationDocument): Promise<void> {
    const navDoc = await this.abcArenaService.loadCustomNavigationDocument;
    if (navDoc) {
      if (result) {
        for (let index = 0; index < navDoc.Groups.length; index++) {
          const group = navDoc.Groups[index];
          if (group.VisibleIndex || group.VisibleIndex === 0) {
            CollectionHelper.insertAt<NavigationGroup>(result.Groups, group.VisibleIndex, group);
          } else {
            CollectionHelper.safePushValueToArray(result.Groups, group);
          }
        }
      } else {
        result = navDoc;
      }
    }
    const prepare = this.abcArenaService.prepareNavigationDocument;
    if (prepare) {
      await prepare(result);
    }
    const profileInfo = await this.profileService.getCurrentProfile(true).toPromise();
    if (result.Groups && profileInfo?.settings?.Commands?.length > 0) {
      for (const grp of result.Groups) {
        if (grp.CommandIdentifier) {
          const command = profileInfo?.settings?.Commands?.find((c: any) => c.name === grp.CommandIdentifier);
          if (!command) {
            grp.VisibleIndex = -1;
            grp.IsVisible = false;
          }
        }
      }
    }
    const vm = new ProfileInfoViewModel(profileInfo);
    if (!vm.settings?.hasVisibleCommand('Arena_Reporting')) {
      this.uiconfig.isReportingDisabled = true;
      this.uiconfig.isQueryingDisabled = true;
    } else {
      this.uiconfig.isReportingDisabled =
        vm.settings?.findCommandDocuments('Arena_Reporting', 'Report')?.allowCustomDocuments === false;
      this.uiconfig.isQueryingDisabled =
        vm.settings?.findCommandDocuments('Arena_Reporting', 'Query')?.allowCustomDocuments === false;
    }
  }

  // This will be probably moved to the core libs
  public loadComponent(component: any, ownerElement: any, data?: any) {
    // this.destroyComponent();
    // 1. Create a component reference from the component
    const componentRef = this.componentFactoryResolver.resolveComponentFactory(component).create(this.injector);
    // 2. Attach component to the appRef
    this.appRef.attachView(componentRef.hostView);
    // 3. Get DOM element from component
    const domElement = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
    if (ownerElement) {
      ownerElement.appendChild(domElement);
    }
    this.componentRef = componentRef;
    if (data) {
      this.componentRef.instance.data = data;
    }
    return { componentRef: componentRef, domElement: domElement };
  }

  public getLoginInfo(): Observable<LoginInfo> {
    return this.userService.getCurrentLoginInfo();
  }

  public removeSpecialCharacter(input: string): string {
    if (
      input.includes(')') ||
      input.includes('(') ||
      input.includes(':') ||
      input.includes('.') ||
      input.includes(')') ||
      input.includes('/') ||
      input.includes("'") ||
      /\s/.test(input) ||
      /[0-9]/.test(input)
    ) {
      input = input
        .replace(')', '')
        .replace('(', '')
        .replace('.', '')
        .replace(':', '')
        .replace('/', '')
        .replace(/['"]+/g, '')
        .replace(/\s/g, '')
        .replace(/[0-9]/g, '');
      return this.removeSpecialCharacter(input);
    } else {
      return input || '';
    }
  }

  public getCurrentProfile(withSettings?: boolean): Observable<ProfileInfo> {
    return this.profileService.getCurrentProfile(withSettings);
  }

  public get areProfilesSupported(): boolean {
    return this.profileService.areProfilesSupported();
  }

  public async refreshObject(entity?: ICoreDataEntity): Promise<void> {
    await this.actions.refreshArenaObject(entity);
  }

  public async openLayoutByObjectType(arenaName: string, objectType: string | number, idList: string[]): Promise<void> {
    const ot = await this.dataService.abcCore.objectSpace.getObjectType(objectType).toPromise();
    const profileInfo = await this.profileService.getCurrentProfile(true).toPromise();
    if ((!arenaName || arenaName === ProfileInfo.defaultArenaName) && profileInfo?.settings?.Arena?.Layouts) {
      let layoutName = this.findArenaLayoutNameByUsage(
        profileInfo?.settings?.Arena?.Layouts,
        ot,
        this.abcArenaService?.context?.layoutUsage,
      );
      if (!layoutName && this.abcArenaService?.context?.layoutUsage !== LayoutUsage.Standard) {
        layoutName = this.findArenaLayoutNameByUsage(profileInfo?.settings?.Arena?.Layouts, ot, LayoutUsage.Standard);
      }
      if (layoutName) {
        this.loadGroupOrLayout(arenaName, layoutName, idList);
      } else {
        this.tryLoadLayoutByObjectType(arenaName, ot, idList);
      }
    } else {
      this.tryLoadLayoutByObjectType(arenaName, ot, idList);
    }
  }

  private async tryLoadLayoutByObjectType(
    arenaName: string,
    objectType: ObjectTypeInfoViewModel,
    idList: string[],
  ): Promise<boolean> {
    const nvm = await this.getNavigation(arenaName ?? this.arenaName);
    if (this.tryLoadLayoutByObjectTypeCore(arenaName, nvm, objectType, idList)) {
      return true;
    }
    this.abcArenaService.arenaStatus({ Status: Status.Error });
    return false;
  }

  private async tryLoadLayoutByObjectTypeCore(
    arenaName: string,
    nvm: NavigationViewModel,
    objectType: ObjectTypeInfoViewModel,
    idList: string[],
  ): Promise<boolean> {
    while (objectType) {
      const resultLayouts = nvm.findLayoutsByObjectType(objectType.name);
      if (resultLayouts?.length) {
        await this.loadGroupOrLayout(arenaName, resultLayouts[0], idList);
        return true;
      }
      objectType = objectType.baseType;
    }
    return false;
  }

  public findArenaLayoutNameByUsage(layouts: any[], objectType: ObjectTypeInfoViewModel, usage: LayoutUsage): string {
    while (objectType) {
      const resultLayout = layouts.find((l) => l.objectTypeName === objectType.name && l.usage == usage);
      if (resultLayout) {
        return resultLayout.fullName;
      }
      objectType = objectType.baseType;
    }
  }

  private objectTypeTextCache: Map<any, string> = new Map();
  public async getObjectTypeTextAsync(objectTypeValue: any): Promise<string> {
    if (CompareHelper.isNullorEmpty(objectTypeValue)) {
      return '';
    }
    let s = this.objectTypeTextCache.get(objectTypeValue);
    if (!s) {
      const otvm: ObjectTypeInfoViewModel = await this.dataService.abcCore.objectSpace
        .getObjectType(StringHelper.extractNumber(ConvertHelper.toString(objectTypeValue)?.split('|')[0]))
        .toPromise();
      s = otvm?.displayText || objectTypeValue;
      this.objectTypeTextCache.set(objectTypeValue, s);
    }
    return s;
  }
}

export interface Notification {
  type: ToastType;
  message: string;
}
